import { recipeApp, type OsRecipe } from '../os-recipes.js';

const SPOTIFY = recipeApp('com.spotify.client');

/** spotify: its app recipes as data (Mac recipes R3). Every entry ships held until its drill lifts it.
 *  Spotify's window is a web view: its Option-Shift chords type a character into a focused field, so
 *  each runs behind the focus guard. Play, pause, next, previous and what's playing are its reflex
 *  verbs, not recipes. The research rows not here are on the R3 review lists. */
export const RECIPES: readonly OsRecipe[] = [
  {
    id: 'spotify.search_in_app', app: SPOTIFY, status: 'held', opens: true,
    title: 'Search Spotify',
    description: 'Search Spotify ("search spotify for coldplay").',
    params: { query: { kind: 'literal', description: 'The exact words to search for', cut: 'query' } },
    steps: [{ op: 'open_url', template: 'spotify:search:{query}' }, { op: 'await_front', timeoutMs: 3000 }],
    tier: 'reversible', verify: 'echo', cues: ['search', 'find', 'look'],
    reply: 'Searched Spotify for {query}.',
  },
  {
    id: 'spotify.open_liked_songs', app: SPOTIFY, status: 'held', opens: true,
    title: 'Show Liked Songs',
    description: 'Show Liked Songs ("open my liked songs").',
    params: {},
    steps: [{ op: 'open_url', template: 'spotify:collection:tracks' }, { op: 'await_front', timeoutMs: 3000 }],
    tier: 'reversible', verify: 'echo', cues: ['liked', 'saved', 'hearted', 'liked songs', 'saved songs', 'songs i liked'],
    reply: 'Opened your Liked Songs in Spotify.',
  },
  {
    id: 'spotify.go_home', app: SPOTIFY, status: 'held', opens: true,
    title: 'Show Spotify Home',
    description: 'Spotify Home ("go to spotify home").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'escape' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'opt+shift+h' }],
    tier: 'reversible', cues: ['home', 'go to spotify home', 'take me home in', 'home dikhao', 'back to spotify home'],
    reply: 'Opened Home in Spotify.',
  },
  {
    id: 'spotify.library', app: SPOTIFY, status: 'held', opens: true,
    title: 'Show Your Library in Spotify',
    description: 'Show Your Library in Spotify ("show my library").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'escape' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'opt+shift+0' }],
    tier: 'reversible', cues: ['library'],
    reply: 'Opened your Library in Spotify.',
  },
  {
    id: 'spotify.made_for_you', app: SPOTIFY, status: 'held', opens: true,
    title: "Show Spotify's Made For You",
    description: 'Made For You ("show made for you").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'escape' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'opt+shift+m' }],
    tier: 'reversible', cues: ['made', 'mixes', 'mix', 'daily mixes', 's made'],
    reply: 'Opened Made For You in Spotify.',
  },
  {
    id: 'spotify.show_queue', app: SPOTIFY, status: 'held', opens: true,
    title: 'Show the queue',
    description: 'Show the queue ("show the queue").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'escape' }, { op: 'focus_not_editable' }, { op: 'key', chord: 'opt+shift+q' }],
    tier: 'reversible', cues: ['queue', 'next', 'coming', 'queue dikhao', 'what s coming'],
    reply: 'Showing your queue in Spotify.',
  },
  {
    id: 'spotify.show_lyrics', app: SPOTIFY, status: 'held', opens: true,
    title: 'Show lyrics',
    description: 'Show lyrics ("show the lyrics").',
    params: {},
    steps: [{ op: 'open_url', template: 'spotify:app:lyrics' }, { op: 'await_front', timeoutMs: 3000 }],
    tier: 'reversible', verify: 'echo', cues: ['lyrics', 'words', 'lyrics dikhao', 'lyrics of this song'],
    reply: 'Showing the lyrics in Spotify.',
  },
  {
    id: 'spotify.quick_search', app: SPOTIFY, status: 'held', opens: true,
    title: "Open Spotify's quick search",
    description: 'Open Spotify\'s quick search ("open spotify search").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+k' }],
    tier: 'reversible', cues: ['search', 'search bar', 'quick search'],
    reply: 'Opened search in Spotify.',
  },
  // A new playlist lands in the account at once and may show on a public profile: it waits for the yes.
  {
    id: 'spotify.new_playlist', app: SPOTIFY, status: 'held',
    title: 'New playlist',
    description: 'New playlist ("new playlist in spotify").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'key', chord: 'cmd+n' }],
    tier: 'irreversible', cues: ['nayi', 'banao', 'new playlist', 'new spotify playlist', 'create a playlist', 'make a playlist', 'nayi playlist banao', 'playlist banao'],
    reply: 'Make a new playlist in your Spotify account?',
  },
  {
    id: 'spotify.back', app: SPOTIFY, status: 'held', opens: true,
    title: 'Go back in Spotify',
    description: 'Go back in Spotify ("go back in spotify").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Go Back'] }],
    tier: 'reversible', cues: ['back', 'previous page', 'back a page'],
    reply: 'Went back in Spotify.',
  },
  {
    id: 'spotify.zoom_in', app: SPOTIFY, status: 'held',
    title: 'Zoom in on Spotify',
    description: 'Zoom in on Spotify ("make spotify bigger").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Zoom In'] }],
    tier: 'reversible', cues: ['zoom', 'bigger', 'larger', 'zoom in on'],
    reply: 'Zoomed in on Spotify.',
  },
  {
    id: 'spotify.fullscreen', app: SPOTIFY, status: 'held',
    title: 'Put Spotify in full screen',
    description: 'Spotify full screen ("make spotify full screen").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'ctrl+cmd+f' }],
    tier: 'reversible', cues: ['full screen', 'fullscreen', 'exit full screen'],
    reply: 'Toggled full screen for Spotify.',
  },  // ── R3c: the catalog rulings' accepts ─────────────────────────────────────────────────────────
  // Views only, the pair of spotify.zoom_in. "zoom" is also the Zoom app's name, so those asks are the model's.
  {
    id: 'spotify.zoom_out', app: SPOTIFY, status: 'held',
    title: 'Zoom out on Spotify',
    description: 'Zoom out on Spotify ("make spotify smaller").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'menu', path: ['View', 'Zoom Out'] }],
    tier: 'reversible', cues: ['smaller', 'chhota', 'spotify smaller'],
    reply: 'Zoomed out on Spotify.',
  },
  {
    id: 'spotify.zoom_reset', app: SPOTIFY, status: 'held',
    title: "Reset Spotify's zoom",
    description: 'Reset Spotify\'s zoom to actual size ("make spotify normal size").',
    params: {},
    steps: [{ op: 'activate' }, { op: 'key', chord: 'cmd+0' }],
    tier: 'reversible', cues: ['actual', 'normal', 'actual size', 'normal size', 'back to normal size'],
    reply: 'Spotify is back to actual size.',
  },
];
